
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Gamepad, Check, HelpCircle, Trophy, Star, ShoppingCart, Store, Users, Banana } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const MonkeyMart = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-800 to-amber-950">
      <Helmet>
        <title>Monkey Mart Game | Fun Store Management Simulation | Pokedle</title>
        <meta
          name="description"
          content="Play Monkey Mart online for free! Manage your own supermarket as a cute monkey. Stock shelves, serve customers, and expand your business in this addictive simulation game."
        />
        <meta name="keywords" content="monkey mart, store management game, business simulation, online game, free browser game" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-4 md:px-8 lg:px-16 bg-gradient-to-r from-amber-800 to-yellow-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://img.freepik.com/free-vector/jungle-background-with-trees-vines_107791-1778.jpg')] opacity-20 bg-cover bg-center"></div>
        <div className="container mx-auto relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in">
                Monkey Mart
              </h1>
              <p className="text-xl text-amber-100 mb-8 max-w-xl animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Become a cute monkey entrepreneur and build your supermarket empire in this addictive store management game!
              </p>
              <a href="#play-game" className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-yellow-500 to-amber-600 text-white font-bold text-lg transition-transform duration-300 hover:scale-105 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                <Gamepad className="mr-2 h-5 w-5" />
                Play Now
              </a>
            </div>
            <div className="md:w-1/2 flex justify-center animate-fade-in" style={{ animationDelay: "0.6s" }}>
              <div className="w-full max-w-md h-64 md:h-80 bg-amber-800 rounded-lg shadow-2xl overflow-hidden relative">
                <div className="absolute inset-0 bg-[url('https://play-lh.googleusercontent.com/egM-S2scPmFII5uuP-uZ0_7gMjBqhW4VC5hKBhGr_CQMLZxTgE-lqXTFBJFZAWNSUw')] bg-cover bg-center opacity-90 hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Game Section */}
      <section id="play-game" className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-800 to-amber-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Play Monkey Mart</h2>
          <div className="game-wrapper w-full aspect-[16/9] max-w-5xl mx-auto rounded-lg overflow-hidden shadow-[0_0_20px_rgba(245,158,11,0.5)]">
            <iframe 
              src="https://gswitch3.github.io/g8/monkey-mart" 
              frameBorder="0" 
              allow="gamepad *;" 
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-950 to-amber-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Game Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <div className="bg-amber-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-yellow-400 mb-4">
                <Store className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Build Your Store</h3>
              <p className="text-amber-200">Start with a small shop and expand it into a thriving supermarket empire.</p>
            </div>
            {/* Feature 2 */}
            <div className="bg-amber-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-yellow-400 mb-4">
                <ShoppingCart className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Stock Management</h3>
              <p className="text-amber-200">Grow crops, restock shelves, and manage your inventory to satisfy customers.</p>
            </div>
            {/* Feature 3 */}
            <div className="bg-amber-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-yellow-400 mb-4">
                <Users className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Customer Service</h3>
              <p className="text-amber-200">Serve a variety of customers, keep them happy, and watch your business grow.</p>
            </div>
            {/* Feature 4 */}
            <div className="bg-amber-800 bg-opacity-50 rounded-lg p-6 hover:bg-opacity-70 transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
              <div className="text-yellow-400 mb-4">
                <Banana className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Monkey Business</h3>
              <p className="text-amber-200">Enjoy adorable monkey characters and a charming, colorful jungle atmosphere.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About the Game */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-900 to-amber-950">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-[0_0_30px_rgba(245,158,11,0.3)]">
                <img 
                  src="https://play-lh.googleusercontent.com/egM-S2scPmFII5uuP-uZ0_7gMjBqhW4VC5hKBhGr_CQMLZxTgE-lqXTFBJFZAWNSUw" 
                  alt="Monkey Mart Gameplay" 
                  className="w-full h-auto"
                />
              </div>
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">About Monkey Mart</h2>
              <div className="space-y-4 text-amber-200">
                <p>
                  Monkey Mart is an engaging supermarket management simulation game where players take on the role of an enterprising monkey running their own grocery store. Developed by independent developer Tiny Dobbins, this charming game combines store management with cute monkey characters in a delightful jungle setting.
                </p>
                <p>
                  The game belongs to the business simulation genre, where you start from a small supermarket and gradually expand the scale of your operation. As a player, you'll perform various tasks such as growing crops, importing goods, stocking shelves, and serving customers. Each decision you make will affect the development of your supermarket, requiring skillful management and strategic thinking.
                </p>
                <p>
                  With its adorable graphics, simple gameplay mechanics, and engaging progression system, Monkey Mart has gained popularity among players of all ages. It's available across multiple platforms including web browsers, Android, and iOS, making it convenient to play anytime, anywhere.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Play */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-950 to-amber-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">How to Play Monkey Mart</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-amber-800 bg-opacity-30 rounded-lg p-6 border border-amber-700">
              <div className="bg-yellow-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">1</div>
              <h3 className="text-xl font-bold text-white mb-3">Start Your Store</h3>
              <p className="text-amber-200">Begin with a small supermarket. Display fruits, vegetables, and other goods to attract your first customers.</p>
            </div>
            {/* Step 2 */}
            <div className="bg-amber-800 bg-opacity-30 rounded-lg p-6 border border-amber-700">
              <div className="bg-yellow-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">2</div>
              <h3 className="text-xl font-bold text-white mb-3">Serve Customers</h3>
              <p className="text-amber-200">Use the arrow keys to move your monkey, collect items, stock shelves, and assist customers to keep them happy.</p>
            </div>
            {/* Step 3 */}
            <div className="bg-amber-800 bg-opacity-30 rounded-lg p-6 border border-amber-700">
              <div className="bg-yellow-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">3</div>
              <h3 className="text-xl font-bold text-white mb-3">Expand & Upgrade</h3>
              <p className="text-amber-200">Reinvest profits to expand your store, add new product lines, hire employees, and create a supermarket empire.</p>
            </div>
          </div>
          
          {/* Tips Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Pro Tips for Monkey Mart Success</h3>
            <div className="bg-amber-800 bg-opacity-30 rounded-lg p-8 border border-amber-700">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-amber-200"><span className="text-white font-semibold">Prioritize Fast-Selling Items:</span> Make sure popular items are always in stock to maximize customer satisfaction and revenue.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-amber-200"><span className="text-white font-semibold">Smart Upgrades:</span> Invest in store expansions and employee training to increase efficiency before spending on decorative items.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-amber-200"><span className="text-white font-semibold">Balance Your Inventory:</span> Offer a diverse range of products to attract different types of customers and maximize sales potential.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-amber-200"><span className="text-white font-semibold">Manage Your Time:</span> Prioritize serving customers who have been waiting the longest to maintain high satisfaction levels.</p>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-400 h-6 w-6 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-amber-200"><span className="text-white font-semibold">Plan Your Layout:</span> Organize your store efficiently to minimize travel time between shelves, storage, and checkout areas.</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Game Mechanics Deep Dive */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-900 to-amber-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Game Mechanics Deep Dive</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Store Management System</h3>
              <p className="text-amber-200 mb-4">
                Monkey Mart features a comprehensive store management system that simulates the real challenges of running a supermarket. Players must balance inventory management, customer service, financial planning, and store expansion to create a thriving business.
              </p>
              <p className="text-amber-200 mb-4">
                The inventory system requires players to constantly monitor stock levels and predict demand for different products. You'll need to grow fruits, restock shelves, and ensure popular items remain available to maintain customer satisfaction.
              </p>
              <p className="text-amber-200">
                As your store grows, you'll unlock new features like employee hiring, training systems, and store upgrades. Each new element adds depth to the gameplay, creating an increasingly complex but rewarding management experience.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">Progression & Economy</h3>
              <p className="text-amber-200 mb-4">
                The game's progression system is built around a carefully balanced in-game economy. Players start with limited resources and must make strategic decisions about where to invest their initial capital for maximum return.
              </p>
              <p className="text-amber-200 mb-4">
                As you serve customers and complete sales, you'll earn money that can be reinvested into your business. The game offers multiple upgrade paths, allowing players to specialize in different areas like product variety, store efficiency, or customer experience.
              </p>
              <p className="text-amber-200">
                The difficulty curve is well-designed, presenting new challenges as your business grows while ensuring the game remains accessible and enjoyable. This creates a satisfying sense of achievement as you transform your small shop into a bustling supermarket empire.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Character Customization */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-950 to-amber-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Monkey Characters & Customization</h2>
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-amber-200 mb-8">
              One of the most charming aspects of Monkey Mart is its adorable monkey characters. As you progress through the game, you can unlock different monkey shopkeepers, each with their own unique appearance and personality traits.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {/* Character Examples */}
              {['#F59E0B', '#D97706', '#B45309', '#92400E'].map((color, index) => (
                <div key={index} className="bg-amber-800 bg-opacity-50 rounded-lg p-4 aspect-square flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full" style={{ backgroundColor: color }}></div>
                </div>
              ))}
            </div>
            <p className="text-amber-200">
              Customize your monkey character with different outfits, accessories, and items that reflect your personality and style. These customization options add a personal touch to your gameplay experience while also providing fun rewards for your progress in building your supermarket empire.
            </p>
          </div>
        </div>
      </section>

      {/* Evolution of Business Simulation Games */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-900 to-amber-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">The Evolution of Business Simulation Games</h2>
          <div className="text-amber-200 space-y-6 max-w-4xl mx-auto">
            <p>
              Monkey Mart joins a rich tradition of business simulation games that have captivated players for decades. The genre traces its roots back to the early days of computer gaming, with titles like "Lemonade Stand" (1979) introducing simple business concepts to players through interactive gameplay.
            </p>
            <p>
              The 1990s saw the rise of more complex business simulations with games like "Theme Park" and "RollerCoaster Tycoon," which allowed players to design and manage entertainment venues. These games combined creative freedom with economic challenges, establishing a formula that would influence countless future titles.
            </p>
            <p>
              The 2000s brought mobile gaming into the mainstream, leading to a new wave of accessible business simulators. Games like "Game Dev Story" and "Tiny Tower" demonstrated that deep simulation mechanics could work on smaller screens with simplified controls, reaching entirely new audiences.
            </p>
            <p>
              Modern business simulation games like Monkey Mart represent the culmination of this evolution. They combine accessible gameplay with deceptively deep management systems, wrapped in appealing visuals and charming themes that appeal to players of all ages and experience levels.
            </p>
            <p>
              What sets Monkey Mart apart in this lineage is its perfect balance of cute characters and engaging gameplay. By casting players as monkey shopkeepers in a jungle setting, the game adds a layer of whimsy to the business simulation formula while maintaining the strategic depth that fans of the genre expect.
            </p>
          </div>
        </div>
      </section>

      {/* Community and Events */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-950 to-amber-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Community Challenges & Events</h2>
          <div className="bg-amber-800 bg-opacity-30 rounded-lg p-8 border border-amber-700 max-w-4xl mx-auto">
            <p className="text-amber-200 mb-8 text-center">
              Join the global Monkey Mart community and participate in special in-game challenges and events. Test your management skills against players from around the world and see how your supermarket empire stacks up!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-amber-800 bg-opacity-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Seasonal Events</h3>
                <p className="text-amber-200">
                  Experience special holiday-themed events throughout the year featuring unique decorations, limited-time products, and exclusive monkey outfits to collect.
                </p>
              </div>
              <div className="bg-amber-800 bg-opacity-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">Management Challenges</h3>
                <p className="text-amber-200">
                  Participate in timed challenges that test your store management skills under specific constraints. Can you reach profit goals while dealing with unexpected obstacles?
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Similar Games */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-900 to-amber-950">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">If You Like Monkey Mart, Try These Games</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-amber-800 bg-opacity-30 rounded-lg overflow-hidden border border-amber-700">
              <div className="h-40 bg-amber-700"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">Game of Farmers</h3>
                <p className="text-amber-200 mb-4">A classic farming simulation where you develop small plots of land into a thriving agricultural business.</p>
                <p className="text-amber-300 font-medium">Similar features: Resource management, business development, farming elements</p>
              </div>
            </div>
            <div className="bg-amber-800 bg-opacity-30 rounded-lg overflow-hidden border border-amber-700">
              <div className="h-40 bg-amber-700"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">Idle Farming Business</h3>
                <p className="text-amber-200 mb-4">A hands-free farming game where you manage and upgrade your farm to earn huge profits over time.</p>
                <p className="text-amber-300 font-medium">Similar features: Business simulation, idle progression, upgrade system</p>
              </div>
            </div>
            <div className="bg-amber-800 bg-opacity-30 rounded-lg overflow-hidden border border-amber-700">
              <div className="h-40 bg-amber-700"></div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">Rhino Rush Stampede</h3>
                <p className="text-amber-200 mb-4">An endless runner game with fun power-ups, epic upgrades, and colorful cartoon graphics.</p>
                <p className="text-amber-300 font-medium">Similar features: Character customization, progression system, cute animal characters</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-950 to-amber-900">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto bg-amber-800 bg-opacity-30 rounded-lg p-8 border border-amber-700">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border-amber-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-yellow-300">
                  What is Monkey Mart?
                </AccordionTrigger>
                <AccordionContent className="text-amber-200">
                  Monkey Mart is a supermarket management simulation game where you play as a monkey entrepreneur. You'll build and expand your own grocery store, stock shelves with products, serve customers, and develop your business from a small shop into a thriving supermarket empire.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-amber-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-yellow-300">
                  How do I play Monkey Mart?
                </AccordionTrigger>
                <AccordionContent className="text-amber-200">
                  The controls are simple: use the arrow keys to move your monkey character around the store. Collect inventory items, stock shelves, serve customers at checkout, and manage your supermarket operations. The game is easy to learn but offers depth as you expand your business.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-amber-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-yellow-300">
                  Is Monkey Mart free to play?
                </AccordionTrigger>
                <AccordionContent className="text-amber-200">
                  Yes, Monkey Mart is completely free to play on our website. No downloads or installations are required—simply visit this page and start playing right away in your browser.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-amber-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-yellow-300">
                  How do I expand my supermarket?
                </AccordionTrigger>
                <AccordionContent className="text-amber-200">
                  As you serve customers and earn money, you can invest in store expansions, new product lines, and additional facilities. Look for upgrade options in the game menu, which will appear as your business grows and you accumulate more funds.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border-amber-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-yellow-300">
                  Can I play Monkey Mart on mobile devices?
                </AccordionTrigger>
                <AccordionContent className="text-amber-200">
                  Yes, Monkey Mart is fully optimized for both desktop and mobile play. The responsive controls work well on touchscreens, making it perfect for gaming on the go.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6" className="border-amber-700">
                <AccordionTrigger className="text-white font-semibold text-lg hover:text-yellow-300">
                  Are there different monkey characters to unlock?
                </AccordionTrigger>
                <AccordionContent className="text-amber-200">
                  Yes, Monkey Mart offers various monkey characters and customization options that you can unlock as you progress through the game. These allow you to personalize your shopkeeper with different appearances, outfits, and accessories.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-amber-950 to-yellow-900">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Build Your Supermarket Empire?</h2>
          <p className="text-xl text-amber-200 mb-10 max-w-2xl mx-auto">
            Put your business skills to the test in Monkey Mart! Manage your store, serve customers, and create the most successful monkey-run supermarket in town.
          </p>
          <a href="#play-game" className="inline-flex items-center px-8 py-4 rounded-full bg-gradient-to-r from-yellow-500 to-amber-600 text-white font-bold text-lg transition-transform duration-300 hover:scale-105">
            <Gamepad className="mr-2 h-5 w-5" />
            Play Monkey Mart Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default MonkeyMart;
